import React, { useEffect, useState } from "react";
import "./ReportMessages.scss";
import { AgingReport, AgingReportBalance, AgingReportMessage, toMonthYear, toPrettyCurrency, toPrettyDate } from "common";
import Select, { StylesConfig } from 'react-select';
import { useUser } from "../store";
import { notify } from "../util/notify";
import ReportMessage from "./ReportMessage";
import { useReports } from "../store/reports";
import { useForm, SubmitHandler, Controller } from "react-hook-form"
import { useConfirmation } from "./ConfirmationProvider";



type ResponseType = {
  id: number;
  text: string;
  sentAt: string;
  deadline?: string;
  recipients: string[];
};

type Thread = {
  id: number;
  text: string;
  sentAt: string;
  deadline?: string;
  targetMonths: string[];
  collapsed: boolean;
  responses: ResponseType[];
};



export default function ReportMessages({ report, onCancel }: { report: AgingReport, onCancel: () => void }) {
  const { invokeApi } = useUser();
  const confirm = useConfirmation();
  const {
    updateReport,
  } = useReports();


  const [users, setUsers] = useState<any[]>([]);
  const [updatedReport, setUpdatedReport] = useState<AgingReport>(report);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [isMessageFormOpen, setIsMessageFormOpen] = useState(false);

  type Inputs = {
    message: string,
    deadline: string,
    months: string[],
    recipients: string[],
  }

  type PaymentInputs = {
    paidMonths: string[],
    paidDate: string,
    checkNumber: string,
  }

  const monthSelectStyles: StylesConfig<{ value: string; label: string }, true> = {
    menuPortal: (base) => ({ ...base, zIndex: 2000 })
  };

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm<Inputs>()

  const paymentMonthOptions = Object.entries(updatedReport?.balances ?? {})
    .filter(([month, balance]) => (balance as AgingReportBalance)?.history?.[0] > 0)
    .map(([month]) => ({ value: month, label: toMonthYear(month) }));

  const paidMonths = Object.entries(updatedReport?.balances ?? {})
    .filter(([, balance]) => (balance as AgingReportBalance)?.paid)
    .sort(([firstMonth], [secondMonth]) => secondMonth.localeCompare(firstMonth));

  const {
    register: registerPayment,
    handleSubmit: handlePaymentSubmit,
    watch: watchPayment,
    setValue: setPaymentValue,
    reset: resetPayment,
    formState: { errors: paymentErrors },
  } = useForm<PaymentInputs>({
    defaultValues: {
      paidMonths: [],
      paidDate: '',
      checkNumber: '',
    }
  });

  const paymentMonths = watchPayment('paidMonths');

  useEffect(() => {
    const selectedBalances = paymentMonths.map((month) => updatedReport?.balances?.[month]);
    const firstBalance = selectedBalances[0];
    const hasMatchingPaymentDetails = selectedBalances.length > 0 && selectedBalances.every((balance) =>
      balance?.paidDate === firstBalance?.paidDate &&
      balance?.checkNumber === firstBalance?.checkNumber
    );
    setPaymentValue('paidDate', hasMatchingPaymentDetails ? firstBalance?.paidDate ?? '' : '');
    setPaymentValue('checkNumber', hasMatchingPaymentDetails ? firstBalance?.checkNumber ?? '' : '');
  }, [paymentMonths, updatedReport, setPaymentValue]);



  useEffect(() => {
    invokeApi({ endpoint: "/getUsersForNonAdmins" }).then((data: any) => {
      setUsers(data);
    });
  }, []);

  useEffect(() => {
    if (isMessageFormOpen) setFocus("message");
  }, [isMessageFormOpen, setFocus]);

  const refreshReport = (report: AgingReport) => {
    setUpdatedReport(report);
    updateReport(report);
  }

  const onSubmit = (input: Inputs) => {
    const { message, deadline, recipients, months } = input;
    const toValues = (entries: any[]) => entries?.map(e => e.value) || [];
    const data = { 
      message, 
      deadline, 
      recipients: toValues(recipients), 
      months: toValues(months), 
      residentName: updatedReport.residentName, 
      billType: updatedReport.billType, 
      index: updatedReport.index
    };

    invokeApi({
      endpoint: "/putReportMessage",
      data,
    }).then((report: AgingReport) => {
      refreshReport(report);
      reset({message: "", deadline: "", recipients: [], months: []});
      setIsMessageFormOpen(false);
      setFocus("message");
      notify.success("Message added!");
    });
  };






  const monthOptions: any[] = [];
  if (updatedReport?.balances) {
    Object.entries(updatedReport.balances)?.forEach(([month, balance]) => {
      const { history } = balance as AgingReportBalance;
      if (history?.[0] === 0) return;
      monthOptions.push({
        value: month,
        label: toMonthYear(month)
      });
    });
  }

  const onReply = (months: string[]) => {
    const newMonths = months.map(m => monthOptions.find(o => o.value === m));
    setValue("months", newMonths);
    setIsPaymentFormOpen(false);
    setIsMessageFormOpen(true);
  }

  const savePayment = (input: PaymentInputs, successMessage: string, paid = true) => {
    if (paid && !input.paidDate) {
      notify.error('Pay date is required when marking a claim as paid.');
      return;
    }
    if (!input.paidMonths.length) {
      notify.error('Select at least one month.');
      return;
    }

    input.paidMonths.reduce<Promise<AgingReport>>(
      (updatePromise, month) => updatePromise.then(() => invokeApi({
        endpoint: '/toggleClaimPaid',
        data: {
          month,
          residentName: updatedReport.residentName,
          billType: updatedReport.billType,
          index: updatedReport.index,
          paid,
          paidDate: input.paidDate || undefined,
          checkNumber: input.checkNumber || undefined,
        },
      })),
      Promise.resolve(updatedReport)
    ).then((report: AgingReport) => {
      refreshReport(report);
      setIsPaymentFormOpen(false);
      resetPayment({
        paidMonths: input.paidMonths,
        paidDate: input.paidDate,
        checkNumber: input.checkNumber,
      });
      notify.success(successMessage);
    });
  };

  const onSubmitPayment = (input: PaymentInputs) => {
    const monthLabels = input.paidMonths.map(toMonthYear).join(', ');
    savePayment(input, `Saved payment status for ${monthLabels}`);
  };

  const onRemovePaidStatus = (month: string) => {
    const monthLabel = toMonthYear(month);
    confirm({
      title: "Remove paid status?",
      message: `Remove the paid status and payment details for ${monthLabel}?`,
      confirmLabel: "Remove status",
    }).then((confirmed) => {
      if (!confirmed) return;
      savePayment(
        { paidMonths: [month], paidDate: '', checkNumber: '' },
        `Removed paid status for ${monthLabel}`,
        false
      );
    });
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-header-copy">
          <h2 className="chat-header-title">{updatedReport.residentName}</h2>
          <span className="chat-header-subtitle">{updatedReport.billType} · Report messages</span>
        </div>
        <button className="chat-close-btn" type="button" onClick={onCancel} aria-label="Close report messages">
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      </div>

      <div className="chat-body">
        <section className="paid-summary" aria-label="Paid claims">
          <div className="paid-summary-heading">
            <h2>Paid claims</h2>
            <span>{paidMonths.length}</span>
          </div>
          {paidMonths.length ? (
            <div className="paid-summary-list">
              {paidMonths.map(([month, rawBalance]) => {
                const balance = rawBalance as AgingReportBalance;
                return (
                  <div className="paid-summary-row" key={month}>
                    <strong className="paid-summary-month">{toMonthYear(month)}</strong>
                    <div className="paid-summary-detail">
                      <span className="paid-summary-label">Paid date</span>
                      <span>{balance.paidDate ? toPrettyDate(balance.paidDate) : 'Not recorded'}</span>
                    </div>
                    <div className="paid-summary-detail">
                      <span className="paid-summary-label">Check #</span>
                      <span>{balance.checkNumber || 'Not recorded'}</span>
                    </div>
                    <button
                      className="paid-remove-btn"
                      type="button"
                      title={`Remove paid status for ${toMonthYear(month)}`}
                      aria-label={`Remove paid status for ${toMonthYear(month)}`}
                      onClick={() => onRemovePaidStatus(month)}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="paid-summary-empty">No paid months recorded.</p>
          )}
        </section>
        <section className="message-list" aria-label="Report messages">
          {!(updatedReport?.messages?.length) ? (
            <div className="no-messages">
              <i className="fa-regular fa-comments" aria-hidden="true" />
              <strong>No messages yet</strong>
              <span>Start the conversation below.</span>
            </div>
          ) : (
            updatedReport.messages.map((message, index) =>
              <ReportMessage {...(index === updatedReport.messages.length - 1 ? { autoFocus: true } : {})} key={message.id} message={message} parentReport={report} onReply={onReply} refreshReport={refreshReport} />
            )
          )}
        </section>
      </div>

      <div className="chat-input">
        <div className="payment-form-section">
          <button
            className="collapsible-form-toggle"
            type="button"
            aria-expanded={isPaymentFormOpen}
            aria-controls="payment-form"
            onClick={() => {
              if (!isPaymentFormOpen) setIsMessageFormOpen(false);
              setIsPaymentFormOpen((isOpen) => !isOpen);
            }}
          >
            <span>
              <i className="fa-solid fa-money-check-dollar" aria-hidden="true" />
              Payment details
            </span>
            <i className={`fa-solid ${isPaymentFormOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
          </button>
          <div
            id="payment-form"
            className={`collapsible-form-content${isPaymentFormOpen ? ' is-open' : ''}`}
            aria-hidden={!isPaymentFormOpen}
          >
            <div className="collapsible-form-content-inner">
              <div className="payment-form">
                <label className="payment-field">
                  <span>Month(s)</span>
                  <Select
                    aria-label="Month"
                    classNamePrefix="message-select"
                    isSearchable={false}
                    isMulti
                    menuPlacement="top"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    styles={monthSelectStyles}
                    options={paymentMonthOptions}
                    placeholder="Select months..."
                    value={paymentMonthOptions.filter((option) => paymentMonths.includes(option.value))}
                    onChange={(options: readonly { value: string; label: string }[]) =>
                      setPaymentValue('paidMonths', options.map((option) => option.value))
                    }
                  />
                </label>
                <label className="payment-field">
                  <span>Pay date *</span>
                  <input
                    type="date"
                    {...registerPayment('paidDate', { required: 'Pay date is required' })}
                  />
                </label>
                <label className="payment-field">
                  <span>Check #</span>
                  <input
                    type="text"
                    {...registerPayment('checkNumber')}
                    placeholder="Optional"
                  />
                </label>
                <button className="send-btn payment-save-btn" onClick={handlePaymentSubmit(onSubmitPayment, () => {
                  setIsMessageFormOpen(false);
                  setIsPaymentFormOpen(true);
                })} type="button">
                  Save payment
                </button>
              </div>
            </div>
          </div>
        </div>
        {paymentErrors.paidDate && <p className="help is-danger">{paymentErrors.paidDate.message}</p>}

        <div className="message-form-section">
          <button
            className="collapsible-form-toggle"
            type="button"
            aria-expanded={isMessageFormOpen}
            aria-controls="message-form"
            onClick={() => {
              if (!isMessageFormOpen) setIsPaymentFormOpen(false);
              setIsMessageFormOpen((isOpen) => !isOpen);
            }}
          >
            <span>
              <i className="fa-regular fa-pen-to-square" aria-hidden="true" />
              New message
            </span>
            <i className={`fa-solid ${isMessageFormOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
          </button>
          <div
            id="message-form"
            className={`collapsible-form-content${isMessageFormOpen ? ' is-open' : ''}`}
            aria-hidden={!isMessageFormOpen}
          >
            <div className="collapsible-form-content-inner message-form-content-inner">
              <textarea
                className={errors.message && "is-danger"}
                placeholder="Write a message for this report..."
                aria-label="Message"
                {...register("message", { required: true })}
              />

              <div className="input-row">
                <label className="message-field">
                  <span>Follow-up date <span className="optional-label">Optional</span></span>
                  <input type="date" {...register("deadline")} />
                </label>
                <label className="message-field">
                  <span>Notify recipients <span className="optional-label">Optional</span></span>
                  <Controller
                    name="recipients"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        classNamePrefix="message-select"
                        aria-label="Notify recipients"
                        placeholder="Choose people..."
                        menuPlacement="top"
                        isMulti
                        options={users?.map((u: any) => ({ value: u?.username, label: u?.name }))}
                      />
                    )}
                  />
                </label>
                <label className="message-field">
                  <span>Related months <span className="optional-label">Optional</span></span>
                  <Controller
                    name="months"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        classNamePrefix="message-select"
                        aria-label="Related months"
                        placeholder="Choose months..."
                        menuPlacement="top"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={monthSelectStyles}
                        isMulti
                        options={monthOptions}
                      />
                    )}
                  />
                </label>

                <span className="move-right">
                  <button className="cancel-btn" type="button" onClick={() => {
                    reset({ message: "", deadline: "", recipients: [], months: [] });
                    setIsMessageFormOpen(false);
                  }}>
                    Cancel
                  </button>
                  <button className="send-btn" type="button" onClick={handleSubmit(onSubmit)}>
                    <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                    Send message
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
